import { Plans, SubscriptionInvoices, Subscriptions } from '@prisma/client';
import API_URL from '../apiconfig';
import {
  CreateInvoiceHoistedUrlResponse,
  CreatePortalSessionResponse,
  CreatePortalSessionType,
  GetStripeSubscriptionResponse,
  GetSubscriptionResponse,
  ResubscribeSubscriptionResponse,
  ResumeSubscriptionResponse,
  UpdateSubscriptionPlanPayload,
} from '../types/subscriptions';
import axios from './api';

export async function fetchSubscription(businessId: string) {
  try {
    const subscription = await axios.get(`${API_URL}/v1/subscriptions`, {
      params: {
        businessId,
      },
    });

    return subscription.data.result as GetSubscriptionResponse;
  } catch (error) {
    throw new Error('Cannot get subscription');
  }
}

export async function fetchStripeSubscription(
  subscriptionId: string,
  businessId: string,
) {
  try {
    const subscription = await axios.get(
      `${API_URL}/v1/subscriptions/${subscriptionId}/stripe`,
      {
        params: {
          businessId,
        },
      },
    );

    return subscription.data.result as GetStripeSubscriptionResponse;
  } catch (error) {
    throw new Error('Cannot get stripe subscription');
  }
}

export async function createPortalSession(
  subscriptionId: string,
  businessId: string,
  type?: CreatePortalSessionType,
) {
  try {
    // get current url with no path, no query params
    const returnUrl = window.location.origin + window.location.pathname;

    const session = await axios.post(
      `${API_URL}/v1/subscriptions/${subscriptionId}/portal-session`,
      {
        businessId,
        type,
        returnUrl,
      },
    );

    return (session.data.result || {}) as CreatePortalSessionResponse;
  } catch (error) {
    throw new Error('Cannot create portal session');
  }
}

export async function fetchSubscriptionInvoices(
  subscriptionId: string,
  businessId: string,
) {
  try {
    const invoices = await axios.get(
      `${API_URL}/v1/subscriptions/${subscriptionId}/invoices`,
      {
        params: {
          businessId,
        },
      },
    );

    return invoices.data.result as SubscriptionInvoices[];
  } catch (error) {
    throw new Error('Cannot get subscription invoices');
  }
}

export async function createInvoiceHoistedUrl({
  subscriptionId,
  businessId,
  invoiceId,
}: {
  subscriptionId: string;
  businessId: string;
  invoiceId: string;
}) {
  try {
    const invoice = await axios.post(
      `${API_URL}/v1/subscriptions/${subscriptionId}/invoices/${invoiceId}`,
      {
        businessId,
      },
    );

    return (invoice.data.result || {}) as CreateInvoiceHoistedUrlResponse;
  } catch (error) {
    throw new Error('Cannot get invoice hoisted url');
  }
}

export async function resumeSubscription(
  subscriptionId: string,
  businessId: string,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/subscriptions/${subscriptionId}/resume`,
      {
        businessId,
      },
    );

    return response.data.result as ResumeSubscriptionResponse;
  } catch (error) {
    throw new Error('Cannot resume subscription');
  }
}

export async function resubsribeSubscription(
  subscriptionId: string,
  businessId: string,
) {
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await axios.post(
      `${API_URL}/v1/subscriptions/${subscriptionId}/resubscribe`,
      {
        businessId,
      },
    );

    return response.data.result as ResubscribeSubscriptionResponse;
  } catch (error) {
    throw error;
  }
}

export async function getAvailablePlans(businessId: string) {
  try {
    const plans = await axios.get(`${API_URL}/v1/subscriptions/plans`, {
      params: {
        businessId,
      },
    });

    return plans.data.result as Plans[];
  } catch (error) {
    throw new Error('Cannot get available plans');
  }
}

export async function changeSubscriptionPlan({
  subscriptionId,
  businessId,
  planId,
}: UpdateSubscriptionPlanPayload & {
  subscriptionId: string;
}) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/subscriptions/${subscriptionId}`,
      {
        businessId,
        planId,
      },
    );

    return response.data.result as Subscriptions;
  } catch (error) {
    throw new Error('Cannot change subscription plan');
  }
}
